import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SUBSCRIBE_TABS, type TabKey, TabStateContext, type TabStateContextType } from './TabStateContext';

type Props = {
  children: React.ReactNode;
};

export const TabStateProvider = ({ children }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab');

  const setActiveTab = useCallback(
    (tab: TabKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'general') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  // Param-derived only: validate against the known tab keys (not visibility) and default to
  // `general`. `PageInner` validates the active tab against the visible-tab list for rendering.
  const activeTab = useMemo<TabKey>(
    () => SUBSCRIBE_TABS.find((tab) => tab === activeTabParam) ?? 'general',
    [activeTabParam],
  );

  const value = useMemo<TabStateContextType>(() => ({ activeTab, setActiveTab }), [activeTab, setActiveTab]);

  return <TabStateContext.Provider value={value}>{children}</TabStateContext.Provider>;
};
